/** Bounded inspection and snapshot helpers shared by JSON viewers. */

export const JSON_EXPANDED_NODE_BUDGET = 500
export const JSON_EXPANDED_DEPTH_BUDGET = 10
export const JSON_DEEP_PARSE_STRING_BUDGET = 64 * 1024
export const JSON_SNAPSHOT_NODE_BUDGET = 220
export const JSON_SNAPSHOT_DEPTH_BUDGET = 8
export const JSON_SNAPSHOT_STRING_LIMIT = 2_000

interface JsonComplexity {
  exceedsNodeBudget: boolean
  exceedsDepthBudget: boolean
  exceedsStringBudget: boolean
  hasReferenceCycle: boolean
}

/** Inspect only enough of a value to decide whether a full tree is safe. */
export function inspectJsonComplexity(value: unknown): JsonComplexity {
  const stack: Array<{ value: unknown; depth: number }> = [{ value, depth: 0 }]
  const seen = new WeakSet<object>()
  let nodes = 0
  let stringCharacters = 0

  while (stack.length > 0) {
    const current = stack.pop()!
    nodes++
    if (nodes > JSON_EXPANDED_NODE_BUDGET) {
      return { exceedsNodeBudget: true, exceedsDepthBudget: false, exceedsStringBudget: false, hasReferenceCycle: false }
    }
    if (current.depth > JSON_EXPANDED_DEPTH_BUDGET) {
      return { exceedsNodeBudget: false, exceedsDepthBudget: true, exceedsStringBudget: false, hasReferenceCycle: false }
    }
    if (typeof current.value === 'string') {
      stringCharacters += current.value.length
      if (stringCharacters > JSON_DEEP_PARSE_STRING_BUDGET) {
        return { exceedsNodeBudget: false, exceedsDepthBudget: false, exceedsStringBudget: true, hasReferenceCycle: false }
      }
      continue
    }
    if (typeof current.value !== 'object' || current.value === null) continue
    if (seen.has(current.value)) {
      return { exceedsNodeBudget: false, exceedsDepthBudget: false, exceedsStringBudget: false, hasReferenceCycle: true }
    }
    seen.add(current.value)

    if (Array.isArray(current.value)) {
      if (nodes + current.value.length > JSON_EXPANDED_NODE_BUDGET) {
        return { exceedsNodeBudget: true, exceedsDepthBudget: false, exceedsStringBudget: false, hasReferenceCycle: false }
      }
      for (let index = current.value.length - 1; index >= 0; index--) {
        stack.push({ value: current.value[index], depth: current.depth + 1 })
      }
      continue
    }

    for (const key in current.value) {
      if (!Object.prototype.hasOwnProperty.call(current.value, key)) continue
      if (nodes + stack.length + 1 > JSON_EXPANDED_NODE_BUDGET) {
        return { exceedsNodeBudget: true, exceedsDepthBudget: false, exceedsStringBudget: false, hasReferenceCycle: false }
      }
      stack.push({ value: (current.value as Record<string, unknown>)[key], depth: current.depth + 1 })
    }
  }

  return { exceedsNodeBudget: false, exceedsDepthBudget: false, exceedsStringBudget: false, hasReferenceCycle: false }
}

export function exceedsExpandedJsonBudget(complexity: JsonComplexity): boolean {
  return complexity.exceedsNodeBudget
    || complexity.exceedsDepthBudget
    || complexity.exceedsStringBudget
    || complexity.hasReferenceCycle
}

/**
 * Build a small, detached representative tree. The regular JSON viewer can
 * still expand this snapshot, but never the original wide/deep object. A
 * sentinel makes truncation visible instead of silently dropping data.
 */
export function createBoundedJsonSnapshot(value: unknown): { value: unknown; truncated: boolean } {
  let nodes = 0
  let truncated = false
  const ancestors = new WeakSet<object>()

  const visit = (input: unknown, depth: number): unknown => {
    if (nodes >= JSON_SNAPSHOT_NODE_BUDGET) {
      truncated = true
      return '… preview truncated …'
    }
    nodes++

    if (typeof input === 'string') {
      if (input.length > JSON_SNAPSHOT_STRING_LIMIT) {
        truncated = true
        return `${input.slice(0, JSON_SNAPSHOT_STRING_LIMIT)}…`
      }
      return input
    }
    if (input === null || typeof input !== 'object') return input
    if (ancestors.has(input)) {
      truncated = true
      return '[Circular]'
    }
    if (depth >= JSON_SNAPSHOT_DEPTH_BUDGET) {
      truncated = true
      return '[Nested value truncated]'
    }

    ancestors.add(input)
    try {
      if (Array.isArray(input)) {
        const result: unknown[] = []
        for (let index = 0; index < input.length; index++) {
          if (nodes >= JSON_SNAPSHOT_NODE_BUDGET) {
            truncated = true
            result.push('… preview truncated …')
            break
          }
          result.push(visit(input[index], depth + 1))
        }
        return result
      }

      const result: Record<string, unknown> = {}
      for (const key in input) {
        if (!Object.prototype.hasOwnProperty.call(input, key)) continue
        if (nodes >= JSON_SNAPSHOT_NODE_BUDGET) {
          truncated = true
          result['…'] = 'preview truncated'
          break
        }
        result[key] = visit((input as Record<string, unknown>)[key], depth + 1)
      }
      return result
    } finally {
      ancestors.delete(input)
    }
  }

  return { value: visit(value, 0), truncated }
}

/** Recursively parse short JSON-looking strings; callers must preflight input. */
export function deepParseJson(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value
  if (depth >= JSON_EXPANDED_DEPTH_BUDGET) return value

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        const parsed = JSON.parse(trimmed)
        return exceedsExpandedJsonBudget(inspectJsonComplexity(parsed)) ? parsed : deepParseJson(parsed, depth + 1)
      } catch {
        return value
      }
    }
    return value
  }
  if (Array.isArray(value)) return value.map(item => deepParseJson(item, depth + 1))
  if (typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value)) result[key] = deepParseJson(val, depth + 1)
    return result
  }
  return value
}

export interface PreparedJsonPreview {
  value: object
  collapsed: boolean
  truncated: boolean
}

export function prepareJsonPreview(value: unknown): PreparedJsonPreview {
  const inputIsLarge = exceedsExpandedJsonBudget(inspectJsonComplexity(value))
  const parsed = inputIsLarge ? value : deepParseJson(value)
  const parsedIsLarge = inputIsLarge || exceedsExpandedJsonBudget(inspectJsonComplexity(parsed))
  const snapshot = parsedIsLarge ? createBoundedJsonSnapshot(parsed) : { value: parsed, truncated: false }
  const normalizedValue = snapshot.value

  let normalized: object
  if (normalizedValue === null || normalizedValue === undefined) normalized = { '(empty)': null }
  else if (typeof normalizedValue !== 'object') normalized = { '(root)': normalizedValue }
  else normalized = normalizedValue as object

  return { value: normalized, collapsed: parsedIsLarge, truncated: snapshot.truncated }
}
